import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { IoCaretBackCircle, IoPersonAddSharp, IoClose } from "react-icons/io5";
import { groupChatFriends, addGroupMembers, groupMemberList, updateGroupChat, groupDetailsById } from "../../redux/ApiSlice/MessageGroupSlice";

const DEFAULT_GROUP_IMAGE = "https://ui-avatars.com/api/?name=Group&background=random";

const MessageGroupInfo = () => {
    const { id } = useParams();
    const groupId = id;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [editMode, setEditMode] = useState(false);
    const [groupName, setGroupName] = useState("");
    const [groupImage, setGroupImage] = useState("");
    const [imagePreview, setImagePreview] = useState("");
    const [imageFile, setImageFile] = useState(null);
    const [showAddMember, setShowAddMember] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [searchFriend, setSearchFriend] = useState("");

    const { groupMemberList: members, groupChatFriendsList, addGroupMembersLoading, updateGroupLoading, groupDetailsByIdResult } = useSelector((state) => state.messageGroup);

    useEffect(() => {
        setTimeout(() => {
            dispatch(groupChatFriends());
        }, 2000);
        if (groupId) {
            setTimeout(() => {
                dispatch(groupDetailsById(groupId));
            }, 1000);
            setTimeout(() => {
                dispatch(groupMemberList(groupId));
            }, 1500);
        }
    }, [dispatch, groupId]);

    useEffect(() => {
        setGroupName(groupDetailsByIdResult?.name || "");
        setImagePreview(groupDetailsByIdResult?.image || DEFAULT_GROUP_IMAGE);
    }, [groupDetailsByIdResult]);

    // --- Edit group handlers ---
    const handleEdit = () => setEditMode(true);
    const handleCancelEdit = () => {
        setEditMode(false);
        setGroupName(groupDetailsByIdResult?.name || "");
        setImagePreview(groupDetailsByIdResult?.image || DEFAULT_GROUP_IMAGE);
        setImageFile(null);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleSaveEdit = async () => {
        const updateData = new FormData();
        updateData.append("name", groupName);
        if (imageFile) updateData.append("image", imageFile);
        await dispatch(updateGroupChat({ groupId, updateData }));
        setEditMode(false);
        setTimeout(() => {
            dispatch(groupDetailsById(groupId));
        }, 1000);
    };

    // --- Add member handlers ---
    const handleAddMember = () => setShowAddMember(true);
    const handleSelectUser = (id) => {
        setSelectedUsers(prev => prev.includes(id) ? prev.filter(uid => uid !== id) : [...prev, id]);
    };

    const handleAddMembersSubmit = async () => {
        if (selectedUsers.length > 0) {
            await dispatch(addGroupMembers({ groupId, user_ids: selectedUsers }));
            setShowAddMember(false);
            setSelectedUsers([]);
            dispatch(groupMemberList(groupId));
        }
    };

    // Filter friends not already in group
    const memberIds = members?.map(m => m.id) || [];
    const filteredFriends = groupChatFriendsList.filter(user =>
        (user.full_name?.toLowerCase().includes(searchFriend.toLowerCase()) || user.username?.toLowerCase().includes(searchFriend.toLowerCase()))
    );

    return (
        <div className="h-full flex flex-col items-center justify-start p-2 relative">
            <div className="w-full flex items-center justify-between mb-4">
                <IoCaretBackCircle
                    onClick={() => navigate(-1)}
                    className="text-[22px] text-black dark:text-white cursor-pointer"
                />
                <button
                    onClick={handleEdit}
                    className="bg-blue-500 text-white px-2 py-0.5 rounded font-semibold text-[12px] hover:bg-blue-600"
                >
                    Edit
                </button>
            </div>

            <div className="flex flex-col items-center w-full">
                <img
                    alt={groupName}
                    src={imagePreview || DEFAULT_GROUP_IMAGE}
                    className="w-32 h-32 rounded-full object-cover border-4 border-gray-300 dark:border-gray-900 mb-2"
                />
                {editMode ? (
                    <div className="w-full flex flex-col items-center">
                        <input
                            type="text"
                            value={groupName}
                            onChange={e => setGroupName(e.target.value)}
                            className="rounded p-2 w-full max-w-xs mb-2 bg-gray-200 dark:bg-gray-900 text-black dark:text-white outline-none"
                        />
                        <div className="w-32">
                            <label className="cursor-pointer bg-black dark:bg-gray-900 text-white px-2 flex justify-center py-1 rounded text-[12px] mb-2">
                                Change Image
                                <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                            </label>
                            <div className="flex gap-x-2">
                                <button
                                    onClick={handleSaveEdit}
                                    disabled={updateGroupLoading}
                                    className="bg-blue-500 text-white w-full px-2 py-1 rounded font-semibold text-[12px] hover:bg-blue-600"
                                >
                                    {updateGroupLoading ? "Saving..." : "Save"}
                                </button>
                                <button
                                    onClick={handleCancelEdit}
                                    className="bg-red-400 dark:bg-red-900 text-white w-full px-2 py-1 rounded font-semibold text-[12px]"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <h2 className="text-lg line-clamp-2 text-center font-bold text-black dark:text-white mb-1">{groupDetailsByIdResult?.name || "Group Chat"}</h2>
                        <p className="text-gray-500 text-sm mb-2">{groupDetailsByIdResult?.member_count ?? (members?.length || 0)} Members</p>
                    </>
                )}
            </div>

            <button
                onClick={handleAddMember}
                className="text-[12px] flex items-center gap-x-2 bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-white px-5 py-1 rounded mt-2 mb-4 hover:bg-gray-200"
            >
                <IoPersonAddSharp className="text-[14px]" /> Add Member
            </button>

            {/* Member list */}
            <div className="w-full max-w-md mt-2">
                <h3 className="text-[16px] font-semibold mb-1 text-black dark:text-white">Group Members</h3>
                <div className="max-h-52 overflow-y-auto border border-gray-300 dark:border-gray-900 rounded p-2 bg-white dark:bg-black">
                    {members && members.length > 0 ? (
                        members.map(user => (
                            <div key={user.id} className="flex items-center gap-2 py-1 border-b last:border-b-0 border-gray-300 dark:border-gray-900">
                                <img
                                    src={user.avatar}
                                    alt={user.full_name}
                                    onClick={() => navigate(`/account/${user.id}`)}
                                    className="w-9 h-9 rounded-full object-cover border border-gray-400 dark:border-gray-900 cursor-pointer"
                                />
                                <div className="flex flex-col">
                                    <p className="text-black dark:text-white text-[12px] font-semibold cursor-pointer flex items-center gap-2">
                                        {user.full_name}
                                    </p>
                                    <div className="flex items-center gap-x-1">
                                        {user.is_owner && (
                                            <p className="text-[10px] text-gray-400 cursor-pointer">Admin |</p>
                                        )}
                                        <p className="text-[10px] text-gray-400 cursor-pointer">{user.username}</p>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="text-gray-400 text-sm">No members found.</p>
                    )}
                </div>
            </div>

            {/* Add member popup */}
            {showAddMember && (
                <div className="absolute inset-0 z-50 p-2 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg w-full max-w-md p-4 relative">
                        <div className="flex items-center gap-x-2 mb-2">
                            <input
                                type="text"
                                value={searchFriend}
                                onChange={e => setSearchFriend(e.target.value)}
                                placeholder="Search friends"
                                className="rounded-full w-full p-1 pl-4 pr-2 outline-none bg-gray-100 dark:bg-gray-800 text-white dark:text-white placeholder:text-[14px]"
                            />
                            <div className="flex items-center justify-center w-7 h-7 bg-gray-200 dark:bg-gray-800 rounded-md cursor-pointer">
                                <IoClose
                                    onClick={() => { setShowAddMember(false); setSelectedUsers([]); }}
                                    className="text-[20px] text-gray-500 hover:text-black dark:hover:text-white"
                                />
                            </div>
                        </div>
                        <div className="max-h-60 overflow-y-auto mb-3">
                            {filteredFriends.map(user => {
                                const alreadyMember = memberIds.includes(user.id);
                                return (
                                    <div
                                        key={user.id}
                                        onClick={() => !alreadyMember && handleSelectUser(user.id)}
                                        className={`flex items-center gap-x-2 p-2 cursor-pointer border-b border-gray-300 dark:border-gray-700 
                                            ${alreadyMember ? 'bg-green-100 dark:bg-green-900 opacity-90' :
                                                selectedUsers.includes(user.id) ? 'bg-blue-100 dark:bg-blue-900' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}
                                        `}
                                    >
                                        <img
                                            alt={user.full_name}
                                            src={user.avatar_url}
                                            className={`w-10 h-10 rounded-full object-cover bg-gray-200 border-2 transition-all duration-150 ${selectedUsers.includes(user.id) ? 'border-blue-500 shadow-md' : 'border-transparent'}`}
                                        />
                                        <div className="flex flex-col">
                                            <p className="text-[15px] text-black dark:text-white line-clamp-1">{user.full_name}</p>
                                            <p className="text-[12px] text-gray-400">{user.username}</p>
                                        </div>
                                        {alreadyMember && <span className="ml-auto text-green-600 text-xs font-semibold">Already in group</span>}
                                    </div>
                                );
                            })}
                            {filteredFriends.length === 0 && (
                                <p className="text-center text-[12px] text-gray-500 py-4">No friends found</p>
                            )}
                        </div>
                        <button
                            disabled={selectedUsers.length === 0 || addGroupMembersLoading}
                            onClick={handleAddMembersSubmit}
                            className={`w-full py-2 rounded-md text-white font-semibold transition-all duration-150 ${selectedUsers.length > 0 && !addGroupMembersLoading ? 'bg-blue-700 hover:bg-blue-800' : 'bg-gray-400 dark:bg-gray-800 cursor-not-allowed'}`}
                        >
                            {addGroupMembersLoading ? 'Adding...' : 'Add Selected'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MessageGroupInfo;